'use client';

import { useEffect, useRef } from 'react';

export default function HeroSection() {
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      const stampEl = heroRef.current.querySelector<HTMLElement>('.parallax-stamp');
      if (stampEl) {
        stampEl.style.transform = `translate(${x * 20}px, ${y * 14}px) rotate(-6deg)`;
      }
    };
    const el = heroRef.current;
    if (el) el.addEventListener('mousemove', handleMouseMove);
    return () => { if (el) el.removeEventListener('mousemove', handleMouseMove); };
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-navy noise-texture pt-28 pb-20"
    >
      {/* Radial glow */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(ellipse, rgba(201,168,76,0.07) 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 right-0 w-[500px] h-[400px]"
          style={{ background: 'radial-gradient(ellipse at bottom right, rgba(201,168,76,0.05) 0%, transparent 70%)' }} />
      </div>

      {/* Floating notary seal decoration */}
      <div className="absolute right-8 md:right-20 top-36 z-10 pointer-events-none parallax-stamp transition-transform duration-300 ease-out">
        <div className="w-28 h-28 md:w-40 md:h-40 rounded-full border-2 border-gold/20 flex items-center justify-center">
          <div className="w-22 h-22 md:w-32 md:h-32 rounded-full border border-dashed border-gold/15 flex items-center justify-center p-4">
            <div className="text-center">
              <p className="font-fraunces text-[0.4rem] md:text-[0.5rem] font-700 uppercase tracking-[0.2em] text-gold/25 leading-tight">
                CERTIFIED<br />NOTARY<br />PUBLIC<br />TEXAS
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Diagonal accent line */}
      <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-transparent via-gold/20 to-transparent" />

      {/* Main content */}
      <div className="relative z-10 max-w-editorial mx-auto px-6 lg:px-12 w-full">
        <div className="max-w-5xl">

          {/* Eyebrow */}
          <p className="section-label mb-8">
            Mobile Notary &amp; Loan Signing Agent — Dallas-Fort Worth
          </p>

          {/* Main headline */}
          <h1 className="hero-headline mb-6">
            We Come<br />
            <span className="text-gold italic">To You.</span><br />
            Anywhere in DFW.
          </h1>

          {/* Subheadline */}
          <p className="font-dm text-[1rem] md:text-[1.1rem] font-400 text-slate-light leading-relaxed max-w-2xl mb-10">
            Professional mobile notary and loan signing agent serving the entire Dallas-Fort Worth metroplex.
            Homes, offices, hospitals, coffee shops — we meet you where you are, on your schedule.
          </p>

          {/* CTA cluster */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-14">
            <a href="#contact" className="cta-primary">
              Book Now
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a href="tel:+14695550183" className="cta-secondary">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M13.5 10.5c-.8-.8-1.9-.8-2.7 0l-.8.8c-1.6-.9-3-2.3-3.9-3.9l.8-.8c.8-.8.8-1.9 0-2.7L5.6 2.6C4.8 1.8 3.7 1.8 2.9 2.6L2 3.5c-.4.4-.6.9-.5 1.5.3 2.5 1.6 4.9 3.5 6.8 1.9 1.9 4.3 3.2 6.8 3.5.6.1 1.1-.1 1.5-.5l.9-.9c.8-.8.8-1.9 0-2.7l-1.2-1.2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Call Now
            </a>
            <a href="#contact" className="cta-secondary">
              Get a Quote
            </a>
          </div>

          {/* Trust signals */}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-8 border-t border-white/8">
            {[
              'Licensed & Bonded in Texas',
              'Available Evenings & Weekends',
              'Same-Day Appointments',
              'Hospital & Jail Signings',
              'Loan Signing Certified',
            ].map((signal) => (
              <span key={signal} className="flex items-center gap-2 font-dm text-[0.72rem] font-600 uppercase tracking-[0.1em] text-slate">
                <span className="w-1.5 h-1.5 rounded-full bg-gold inline-block flex-shrink-0" />
                {signal}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
